import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Textarea } from "../../../../../../components/ui/textarea";
import Button from "../../../../../../components/shared/Button";
import Sheet from "../../../../../../components/shared/Sheet";
import BaseForm, { Field } from "../../../../../../components/shared/Form";
import SectionDetails from "./SectionDetails";
import SectionsDisplay from "./SectionsDisplay";
import useApi from "../../../../../../utils/api";
import { calculateSections } from "../../../hooks/form/useCalculatedSections";
import { getErrorMessage } from "../../../../../../utils/helpers/errors";
import { useResultDialog } from "./ResultDialog/useResultDialog";
import { createShiftTradeSchema, type ShiftTradeFormValues } from "./zodValidation";
import { withLoadingToast } from "../../../helpers/withLoadingToast";
import type { ShiftResult } from "./ResultDialog/types";
import type { CalculatedSection, ShiftTradeItem, ShiftTradeProps, ShiftTradeRequestPayload } from "./types";

function ShiftTrade({ isOpen, close, values, view }: ShiftTradeProps) {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const [selectedUserShifts, setSelectedUserShifts] = useState<ShiftTradeItem[]>([]);
    const { authPost } = useApi();

    const title = useMemo(() => {
        if (values?.type === "swap") {
            return t("swap-shift");
        } else {
            return t("offer-shift");
        }
    }, [values?.type, t]);

    const sections = useMemo((): CalculatedSection[] => {
        return calculateSections(values?.event?.sections, values?.event?.startDate) ?? [];
    }, [values?.event]);

    const schema = useMemo(() => createShiftTradeSchema({ t }), [t]);

    const form = useForm<ShiftTradeFormValues>({
        defaultValues: { reason: "" },
        resolver: zodResolver(schema)
    });

    const onClose = useCallback(() => {
        form.reset();
        setSelectedUserShifts([]);
        setError(null);
        close();
    }, [close, form]);

    const { open: openResultDialog, modal: resultDialog } = useResultDialog({ onClose });

    const mutation = useMutation({
        ...withLoadingToast(t("sending-request")),
        mutationFn: (data: ShiftTradeRequestPayload) => {
            const url = `/schedules/v2/${values.type}-shifts`;
            return authPost(url, { data });
        },
        onSuccess: (response?: ShiftResult[]) => {
            if (response?.length > 0) {
                openResultDialog(response);
                return;
            }

            toast.success(
                values?.type === "swap"
                    ? t("swap-request-created-successfully")
                    : t("offer-request-created-successfully")
            );
            onClose();
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const onFinish = useCallback((value: ShiftTradeFormValues) => {
        if (!selectedUserShifts?.length) {
            toast.error(t("cant-create-trade-request", { trade: values?.type }));
            return;
        }

        const data: ShiftTradeRequestPayload = {
            originalShift: values?.event?.id,
            reason: value?.reason
        };

        if (values?.type === "swap") {
            data.swapShifts = selectedUserShifts.map((shift) => shift.id);
        }

        if (values?.type === "offer") {
            data.users = selectedUserShifts.map((user) => user.id);
        }

        mutation.mutate(data);
    }, [selectedUserShifts, values, mutation, t]);

    return (
        <>
            <Sheet open={isOpen} close={onClose} title={title}>
                <BaseForm form={form} onFinish={onFinish} className="tw-h-full">
                    <div style={{ height: "calc(100% - 50px)" }}>
                        <div className="tw-mb-5">
                            <SectionDetails sections={sections} />
                        </div>

                        <Field name="reason">
                            <Textarea
                                rows={3}
                                className="tw-resize-none field-sizing-fixed"
                                placeholder={values?.type === "swap"
                                    ? t("swap-reason")
                                    : t("offer-reason")
                                }
                            />
                        </Field>

                        {sections?.length > 0 && (
                            <SectionsDisplay
                                sections={sections}
                                type={values?.type}
                                shiftId={values?.event?.id}
                                view={view}
                                height={200}
                                error={error}
                                setError={setError}
                                selectedUserShifts={selectedUserShifts}
                                setSelectedUserShifts={setSelectedUserShifts}
                            />
                        )}
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-2 tw-justify-end">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            disabled={mutation.isPending}
                            loading={mutation.isPending}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            disabled={mutation.isPending}
                            loading={mutation.isPending}
                        >
                            {values?.type === "swap" ? t("swap-shift") : t("offer-shift")}
                        </Button>
                    </div>
                </BaseForm>
            </Sheet>

            {resultDialog}
        </>
    );
}

export default ShiftTrade;
